import ConditionalToolTip from "components/design/conditionalToolTip";
import { openDownloadUrl } from "util/openDownloadUrl";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { CaretDown } from "components/icons/CaretDown";
import { LinkIcon } from "components/icons/LinkIcon";
import { RecycleIcon } from "components/icons/RecycleIcon";
import { FOLDER_TYPE } from "constants/documents";
import { useAnalytics } from "hooks/useAnalytics";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import React, { useContext, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Gridview from "static/images/GridView.svg";
import Listview from "static/images/ListView.svg";
import {
  useExportFolderMutation,
  useRenameFileFolderMutation,
} from "store/apis/documents";
import { BackendError } from "types/utils/error";
import { getFileFolderShareLink } from "util/getFileFolderShareLink";
import { AddActionDropdown } from "./AddActionDropdown";
import { DocumentContext } from "./documentContext";
import { MoveDocModal } from "./documentsModal/MoveDocModal";
import { RenameFileFolderModal } from "./documentsModal/renameFileFolderModal";

type DocHeaderActionsProps = {
  groupId: string;
  openAddFolderModal: () => void;
  setLoad: React.Dispatch<React.SetStateAction<boolean>>;
  ParentId: string | null;
  setLoadingFileUploadStart: (value: boolean) => void;
  uploadPayload: any;
  open: () => void;
  listView: boolean;
  changeViewType: () => void;
  setUploadPayload: (payload: any) => void;
  setNewFolderName: (name: string) => void;
  entityFileAndFolder: Array<{
    uuid: string;
    name: string;
    type: string;
    parent_uuid: string;
    is_locked: boolean;
    isChecked?: boolean;
    is_private?: boolean;
  }>;
  isInsidePrivateFolder: boolean;
  isRootFolder: boolean;
};

export const DocHeaderActions = ({
  groupId,
  openAddFolderModal,
  setLoad,
  ParentId,
  setLoadingFileUploadStart,
  uploadPayload,
  open,
  listView,
  changeViewType,
  setUploadPayload,
  setNewFolderName,
  entityFileAndFolder,
  isInsidePrivateFolder,
  isRootFolder,
}: DocHeaderActionsProps) => {
  const { trackEvent } = useAnalytics();
  const {
    selectedFiles,
    setSelectedFiles,
    setCurrentPageNo,
    setEntityFileAndFolder,
    handleFolderClick,
  } = useContext(DocumentContext);
  const query = useQuery();
  const {
    alertToast,
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();
  const searchTerm = query.get("search");
  const selectedFileIds = useMemo(
    () => selectedFiles.map(({ uuid }: { uuid: string }) => uuid),
    [selectedFiles]
  );
  const moveFileModal = useModal();
  const [renameModalShow, setRenameModalShow] = useState(false);
  const [exportFolder] = useExportFolderMutation();
  const selectedFileData = useMemo(() => [...selectedFiles], [selectedFiles]);
  const [renameFileAndFolder] = useRenameFileFolderMutation();
  const { isAdmin } = useRoleBasedView();
  const { copyToClipboard } = useCopyToClipboard();

  const containsPrivateDocuments = selectedFileData.some(
    ({ is_private }) => is_private
  );
  const history = useHistory();
  const { pathname } = useLocation();
  const { link } = useConstructInternalLink();
  const baseDocumentsPath = pathname.replace(/\/documents\/.*/, "/documents");

  const goToRecycleBin = () => {
    history.push(link(`${baseDocumentsPath}/recyclebin`));
  };

  const copyFolderLink = () => {
    if (!ParentId) {
      return;
    }

    copyToClipboard(
      getFileFolderShareLink({
        groupId,
        itemId: ParentId,
        itemType: FOLDER_TYPE,
      })
    );
  };

  const exportAllDocuments = async (exportToMail: boolean) => {
    const loadingtoastId = loadingToast({
      title: "Preparing Export",
      message: "This may take upto one minute",
    });
    try {
      const response = await exportFolder({
        groupId,
        export_to_mail: exportToMail,
      }).unwrap();

      if (!exportToMail) {
        const { download_url } = response;
        successToast({
          title: "Export Completed",
        });
        openDownloadUrl(download_url);
      } else {
        successToast({
          title: "Export in progress",
          message: "Documents will be sent to your email once done",
        });
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    } finally {
      dismissToast(loadingtoastId);
    }
  };

  const renameFolder = async ({ file_name }: { file_name: string }) => {
    try {
      const data = await renameFileAndFolder({
        groupId,
        payload: { file_name },
        fileId: selectedFileData[0]?.uuid,
      }).unwrap();
      successToast({ message: `Renamed to ${data.name}` });
      const updatedDocuments = entityFileAndFolder.map((doc) =>
        doc.uuid === data.uuid ? data : doc
      );
      setEntityFileAndFolder(updatedDocuments);
      setNewFolderName(data.name);
      setSelectedFiles([]);
      setRenameModalShow(false);
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const handleMoveSuccess = () => {
    setLoad((prev) => !prev);
    setSelectedFiles([]);
  };

  const showCopyFolderLink = Boolean(ParentId) && !searchTerm && !isRootFolder;

  return (
    <span
      className="viewChangeButtonContainer t-flex gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      {showCopyFolderLink && (
        <ConditionalToolTip condition="Copy link to share this folder">
          <span>
            <Button
              customType="icon"
              size="small"
              onClick={copyFolderLink}
              aria-label="Copy link"
            >
              <LinkIcon />
            </Button>
          </span>
        </ConditionalToolTip>
      )}
      {isAdmin && (
        <Button
          leftIcon={<RecycleIcon />}
          size="small"
          onClick={goToRecycleBin}
        >
          Recycle Bin
        </Button>
      )}
      <ToggleGroup
        value={listView ? "list" : "grid"}
        onValueChange={changeViewType}
        size="small"
      >
        <ConditionalToolTip condition="Grid view">
          <ToggleGroupItem value="grid">
            <img src={Gridview} alt="Grid view" />
          </ToggleGroupItem>
        </ConditionalToolTip>
        <ConditionalToolTip condition="List view">
          <ToggleGroupItem value="list">
            <img src={Listview} alt="List view" />
          </ToggleGroupItem>
        </ConditionalToolTip>
      </ToggleGroup>

      <ConditionalToolTip condition="Export documents">
        <span>
          <Dropdown.Root>
            <Dropdown.Trigger asChild className="t-group">
              <div>
                <Button size="small" title="Download selected">
                  <div className="t-flex t-items-center t-gap-1.5">
                    Download
                    <div className="group-data-state-open:t-rotate-180">
                      <CaretDown />
                    </div>
                  </div>
                </Button>
              </div>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content sideOffset={4} align="end" side="bottom">
                <ExportOptions
                  onLocalDownload={() => exportAllDocuments(false)}
                  onMailExport={() => exportAllDocuments(true)}
                />
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        </span>
      </ConditionalToolTip>
      <AddActionDropdown
        setLoadingFileUploadStart={setLoadingFileUploadStart}
        groupId={groupId}
        setLoad={setLoad}
        openAddFolderModal={openAddFolderModal}
        ParentId={ParentId}
        uploadPayload={uploadPayload}
        setUploadPayload={setUploadPayload}
        open={open}
        isInsidePrivateFolder={isInsidePrivateFolder}
      />

      {renameModalShow && (
        <RenameFileFolderModal
          renameModalShow={renameModalShow}
          closeRenameModal={() => setRenameModalShow(false)}
          itemType={selectedFileData[0]?.type}
          folderName={selectedFileData[0]?.name}
          renameFolder={renameFolder}
        />
      )}

      <MoveDocModal
        isOpen={moveFileModal.isOpen}
        close={moveFileModal.close}
        itemIds={selectedFileIds}
        onMoveSuccess={handleMoveSuccess}
        containsPrivateDocuments={containsPrivateDocuments}
      />
    </span>
  );
};
